import { Injectable, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { NavigationService } from './navigation.service';

@Injectable({ providedIn: 'root' })
export class SwipeNavigationService implements OnDestroy {
    private swipeStartX: number | null = null;
    private swipeStartY: number | null = null;
    private readonly swipeTriggerDistance = 50;
    private subscriptions: Subscription[] = [];

    constructor(
        private navigationService: NavigationService,
    ) { }

    registerSwipeArea(element: HTMLElement): void {
        const touchMove$ = fromEvent<TouchEvent>(element, 'touchmove').subscribe((event) => {
            if (this.swipeStartX == null) return;
            if ((event.target as HTMLElement)?.closest('[data-no-nav-swipe]')) return;

            const currentX = event.touches[0].clientX;
            const diffX = currentX - this.swipeStartX;

            if (Math.abs(diffX) >= this.swipeTriggerDistance) {
                event.preventDefault();
                this.preventScrolling(element);
            } else {
                this.allowScrolling(element);
            }
        });

        const touchCancel$ = fromEvent<TouchEvent>(element, 'touchcancel').subscribe(() => {
            this.swipeStartX = null;
            this.swipeStartY = null;
            this.allowScrolling(element);
        });

        const touchStart$ = fromEvent<TouchEvent>(element, 'touchstart').subscribe((event) => {
            if ((event.target as HTMLElement)?.closest('[data-no-nav-swipe]')) {
                this.swipeStartX = null;
                return;
            }
            this.swipeStartX = event.touches[0].clientX;
            this.swipeStartY = event.touches[0].clientY;
        });

        const touchEnd$ = fromEvent<TouchEvent>(element, 'touchend').subscribe((event) => {
            this.allowScrolling(element);
            if (this.swipeStartX == null) return;
            if ((event.target as HTMLElement)?.closest('[data-no-nav-swipe]')) return;

            const endX = event.changedTouches[0].clientX;
            const endY = event.changedTouches[0].clientY;

            const diffX = endX - this.swipeStartX!;
            const diffY = endY - this.swipeStartY!;

            if (Math.abs(diffX) >= this.swipeTriggerDistance && Math.abs(diffX) > Math.abs(diffY)) {
                const direction = diffX > 0 ? 'left' : 'right';
                this.navigationService.navigateBySwipe(direction);
            }

            this.swipeStartX = null;
            this.swipeStartY = null;
        });

        this.subscriptions.push(touchStart$, touchEnd$, touchMove$, touchCancel$);
    }

    destroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    private preventScrolling(element: HTMLElement): void {
        element.addEventListener('wheel', this.preventScrollEvent, { passive: false });
        element.addEventListener('touchmove', this.preventScrollEvent, { passive: false });
    }

    private preventScrollEvent(event: Event): void {
        event.preventDefault();
    }

    private allowScrolling(element: HTMLElement): void {
        element.removeEventListener('wheel', this.preventScrollEvent);
        element.removeEventListener('touchmove', this.preventScrollEvent);
    }

    ngOnDestroy() {
        this.destroy();
    }
}
